import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, Row, Col, Button, Spin, Alert, Typography, Tag, 
  Collapse, Badge, Space, Modal 
} from 'antd';
import { 
  ArrowLeftOutlined, EditOutlined, 
  SoundOutlined, VideoCameraOutlined 
} from '@ant-design/icons';
import { getExerciseById, getExerciseAlternatives } from '../../services/exercises';
import { resolveMedia } from '../../utils/media';
import '../../styles/global.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const ExercisesViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [exerciseData, setExerciseData] = useState(null);
  const [alternativesList, setAlternativesList] = useState([]);
  const [error, setError] = useState(null);

  // Estado para reproducir multimedia en modal
  const [playerModal, setPlayerModal] = useState({ visible: false, url: null, type: null, title: '' });

  useEffect(() => {
    if (!id) {
      setError('ID no proporcionado');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [exercise, alternatives] = await Promise.all([
          getExerciseById(id),
          getExerciseAlternatives(id).catch(() => [])
        ]);

        setExerciseData(exercise);
        setAlternativesList(exercise.alternatives?.length ? exercise.alternatives : alternatives);
      } catch (err) {
        console.error('Error al cargar ejercicio:', err);
        setError('No se pudo cargar la información del ejercicio');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleEdit = () => navigate(`/exercises/${id}/edit`);
  const handleBack = () => navigate('/exercises/all');

  const openPlayer = (rawPath, type, title) => {
    if (!rawPath) return;
    setPlayerModal({
      visible: true,
      url: resolveMedia(rawPath),
      type,
      title
    });
  };

  const closePlayer = () => {
    setPlayerModal({ visible: false, url: null, type: null, title: '' });
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !exerciseData) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error"
          description={error || 'Datos no encontrados'}
          type={error ? 'error' : 'warning'}
          showIcon
          action={<Button size="small" onClick={handleBack}>Volver</Button>}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 24px 24px 24px', backgroundColor: '#fafafa', minHeight: '100vh' }}>
      
      {/* HEADER */}
      <div style={{ padding: '24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined style={{ fontSize: '20px' }} />} 
            onClick={handleBack}
          />
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Catálogo de Ejercicios</Text>
            <Title level={2} style={{ margin: 0 }}>{exerciseData.title || exerciseData.name}</Title>
          </div>
        </div>

        <Button 
          type="primary" 
          icon={<EditOutlined />} 
          onClick={handleEdit}
          style={{ borderRadius: 8 }}
        >
          Editar ejercicio
        </Button>
      </div>

      <Row gutter={[24, 24]}>
        
        {/* COLUMNA IZQUIERDA: DATOS Y VARIANTES */}
        <Col xs={24} lg={16}>
          
          {/* 1. INFORMACIÓN GENERAL */}
          <Card style={{ borderRadius: 12, marginBottom: 24 }} bordered={false}>
            <Title level={4}>Información general</Title>
            
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12}>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>¿Es Unilateral?</Text>
                <Tag color={exerciseData.is_unilateral ? 'blue' : 'default'} style={{ marginTop: 4 }}>
                  {exerciseData.is_unilateral ? 'Sí (Unilateral)' : 'No (Bilateral)'}
                </Tag>
              </Col>

              <Col xs={24} sm={12}>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Tipos de ejercicio</Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {exerciseData.types?.length > 0 ? (
                    exerciseData.types.map(t => <Tag key={t.id} color="purple">{t.name}</Tag>)
                  ) : <Text type="secondary">N/A</Text>}
                </Space>
              </Col>

              <Col xs={24} sm={12}>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Músculos Primarios</Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {exerciseData.primary_muscles?.length > 0 ? (
                    exerciseData.primary_muscles.map(m => <Tag key={m.id} color="green">{m.name}</Tag>)
                  ) : <Text type="secondary">N/A</Text>}
                </Space>
              </Col>

              <Col xs={24} sm={12}>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Músculos Secundarios</Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {exerciseData.secondary_muscles?.length > 0 ? (
                    exerciseData.secondary_muscles.map(m => <Tag key={m.id} color="cyan">{m.name}</Tag>)
                  ) : <Text type="secondary">N/A</Text>}
                </Space>
              </Col>

              <Col xs={24}>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Estilos de Entrenamiento</Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {exerciseData.training_styles?.length > 0 ? (
                    exerciseData.training_styles.map(s => <Tag key={s.id} color="orange">{s.name}</Tag>)
                  ) : <Text type="secondary">N/A</Text>}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 2. VARIANTES Y MULTIMEDIA POR EJECUTOR */}
          <Card style={{ borderRadius: 12, marginBottom: 24 }} bordered={false}>
            <Title level={4}>Variantes de equipo</Title>
            
            {exerciseData.variants && exerciseData.variants.length > 0 ? (
              <Collapse accordion style={{ background: '#fff', borderRadius: 8, marginTop: 16 }}>
                {exerciseData.variants.map((variant) => {
                  const isEnabled = variant.status === 'enabled';
                  const equipmentName = variant.equipment?.name || variant.equipment?.title || 'Equipo';

                  return (
                    <Panel 
                      key={variant.id} 
                      header={<b>{equipmentName}</b>}
                      extra={
                        <Badge 
                          color={isEnabled ? 'green' : 'orange'} 
                          text={
                            <span style={{ color: isEnabled ? '#52c41a' : '#faad14', fontWeight: 500 }}>
                              {isEnabled ? 'Habilitado' : 'Pendiente'}
                            </span>
                          } 
                        />
                      }
                    >
                      {/* Instrucciones */}
                      <div style={{ marginBottom: 20 }}>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Instrucciones de ejecución</Text>
                        <Text style={{ fontSize: 14 }}>{variant.instructions || 'Sin instrucciones detalladas.'}</Text>
                      </div>

                      {/* Ejecutores y Multimedia */}
                      <Text strong style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 12 }}>
                        MULTIMEDIA POR EJECUTOR
                      </Text>

                      {variant.executors && variant.executors.length > 0 ? (
                        variant.executors.map((exec) => {
                          const executorName = exec.executor?.name || exec.executor_name || 'Ejecutor';
                          const hasMedia = exec.vertical_video_url || exec.horizontal_video_url || exec.audio_url;

                          return (
                            <Card 
                              key={exec.id || exec.executor_id} 
                              type="inner" 
                              title={<Text size="small" strong>{executorName}</Text>}
                              style={{ marginBottom: 12, borderRadius: 8 }}
                            >
                              {hasMedia ? (
                                <Space wrap>
                                  {exec.vertical_video_url && (
                                    <Button 
                                      icon={<VideoCameraOutlined />} 
                                      onClick={() => openPlayer(exec.vertical_video_url, 'video', `Video Vertical — ${executorName}`)}
                                    >
                                      Video Vertical
                                    </Button>
                                  )}

                                  {exec.horizontal_video_url && (
                                    <Button 
                                      icon={<VideoCameraOutlined />} 
                                      onClick={() => openPlayer(exec.horizontal_video_url, 'video', `Video Horizontal — ${executorName}`)}
                                    >
                                      Video Horizontal
                                    </Button>
                                  )}

                                  {exec.audio_url && (
                                    <Button 
                                      icon={<SoundOutlined />} 
                                      onClick={() => openPlayer(exec.audio_url, 'audio', `Audio — ${executorName}`)}
                                    >
                                      Audio
                                    </Button>
                                  )}
                                </Space>
                              ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>Sin multimedia registrada</Text>
                              )}
                            </Card>
                          );
                        })
                      ) : (
                        <Text type="secondary">No hay ejecutores asociados a esta variante.</Text>
                      )}
                    </Panel>
                  );
                })}
              </Collapse>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                No hay variantes registradas para este ejercicio.
              </Text>
            )}
          </Card>
        </Col>

        {/* COLUMNA DERECHA: ALTERNATIVAS */}
        <Col xs={24} lg={8}>
          <Card style={{ borderRadius: 12 }} bordered={false}>
            <Title level={4}>Ejercicios Alternativos</Title>
            
            {alternativesList && alternativesList.length > 0 ? (
              <div style={{ marginTop: 16 }}>
                {alternativesList.map((item) => {
                  const altId = item.alternative_id || item.alternative?.id;
                  const altTitle = item.alternative?.title || item.label || 'Ejercicio Alternativo';
                  const eqName = item.equipment?.name || item.equipment_name;

                  return (
                    <div 
                      key={item.id} 
                      style={{ 
                        padding: '12px 0', 
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justify: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <a 
                          href={`/exercises/view/${altId}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/exercises/view/${altId}`);
                          }}
                          style={{ fontWeight: 600, color: '#1890ff' }}
                        >
                          {altTitle}
                        </a>
                        {eqName && (
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            Equipo: {eqName}
                          </Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                No se han registrado alternativas para este ejercicio.
              </Text>
            )}
          </Card>
        </Col>

      </Row>

      {/* MODAL DE REPRODUCCIÓN MULTIMEDIA */}
      <Modal
        open={playerModal.visible}
        title={playerModal.title}
        footer={null}
        destroyOnClose
        onCancel={closePlayer}
        centered
        styles={{ body: { padding: 0 } }}
      >
        {playerModal.type === 'video' ? (
          <video 
            src={playerModal.url} 
            controls 
            autoPlay 
            style={{ width: '100%', maxHeight: '70vh', outline: 'none', display: 'block', backgroundColor: '#000' }} 
          />
        ) : (
          <div style={{ padding: '32px 24px', background: '#fafafa', textAlign: 'center' }}>
            <audio src={playerModal.url} controls autoPlay style={{ width: '100%', outline: 'none' }} />
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ExercisesViewPage;